import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminAPI } from '../lib/api';
import { ArrowRight, Shield, Zap, Search, Flame, BarChart3, Database, ChevronRight, Lock, CheckCircle2, Users } from 'lucide-react';

const FeatureCard = ({ icon: Icon, title, description, delay }) => (
  <div className={`fade-in-up group p-1`} style={{ animationDelay: `${delay}ms` }}>
    <div className="relative h-full bg-white/50 backdrop-blur-xl border border-white/20 p-8 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all duration-500 overflow-hidden">
      <div className="absolute top-0 right-0 p-8 opacity-5 transition-opacity duration-500 group-hover:opacity-10 scale-150 rotate-12">
        <Icon size={120} />
      </div>
      <div className="h-14 w-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-blue-500/30 mb-6 transform group-hover:-translate-y-2 transition-transform duration-500">
        <Icon size={28} />
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-3">{title}</h3>
      <p className="text-gray-600 leading-relaxed">{description}</p>
      <div className="mt-6 flex items-center text-blue-600 font-semibold opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300">
        Tìm hiểu thêm <ChevronRight size={18} className="ml-1" />
      </div>
    </div>
  </div>
);

export default function Landing() {
  const [siteSettings, setSiteSettings] = useState({});

  useEffect(() => {
    adminAPI.getSiteSettings().then(res => setSiteSettings(res.data || {})).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#fafcff] flex flex-col font-sans overflow-x-hidden selection:bg-blue-500/30">
      {/* Dynamic Background */}
      <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-400/20 blur-[120px] rounded-full animate-blob"></div>
        <div className="absolute top-[20%] right-[-10%] w-[35%] h-[40%] bg-purple-400/20 blur-[120px] rounded-full animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-[-10%] left-[20%] w-[40%] h-[40%] bg-indigo-400/20 blur-[120px] rounded-full animate-blob animation-delay-4000"></div>
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.02] mix-blend-overlay"></div>
      </div>

      {/* Navbar */}
      <nav className="relative z-50 flex items-center justify-between px-6 py-6 md:px-12 xl:px-24">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/30">
            <Flame className="text-white" size={24} />
          </div>
          <span className="text-2xl font-black bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-600">
            {siteSettings.site_name?.split(' ')[0] || 'TriTechz'}
          </span>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/login" className="hidden sm:flex text-gray-600 hover:text-gray-900 font-semibold px-4 py-2 transition-colors">
            Đăng nhập
          </Link>
          <Link to="/register" className="group relative px-6 py-2.5 bg-gray-900 text-white font-semibold rounded-full overflow-hidden shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900">
             <span className="relative z-10 flex items-center gap-2">
               Bắt đầu ngay <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
             </span>
             <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <main className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 pt-20 pb-32 text-center md:px-12 lg:pt-32 lg:pb-40">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/60 border border-gray-200 backdrop-blur-md shadow-sm mb-8 animate-fade-in-up">
          <span className="flex h-2 w-2 rounded-full bg-green-500 animate-pulse"></span>
          <span className="text-sm font-semibold text-gray-700">Nền tảng Quản trị & Phân tích Dữ liệu v2.0</span>
        </div>
        
        <h1 className="text-5xl md:text-7xl lg:text-8xl font-black text-gray-900 tracking-tight max-w-5xl leading-[1.1] mb-8 animate-fade-in-up" style={{ animationDelay: '100ms' }}>
          Tối Ưu Hóa <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-indigo-500 to-purple-600">Phân Phối</span> & Dữ Liệu Khách Hàng.
        </h1>
        
        <p className="text-lg md:text-xl text-gray-600 max-w-2xl mb-12 animate-fade-in-up leading-relaxed" style={{ animationDelay: '200ms' }}>
          Giải pháp công nghệ chuyên sâu dành cho Doanh nghiệp, Marketer và KOLs. Tự động hóa quá trình thu thập, đối soát và phân tích dữ liệu mạng xã hội bằng Trí tuệ Nhân tạo.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto animate-fade-in-up" style={{ animationDelay: '300ms' }}>
          <Link to="/register" className="h-14 px-8 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-2xl flex items-center justify-center gap-2 shadow-xl shadow-blue-500/20 hover:shadow-blue-500/40 hover:-translate-y-1 transition-all duration-300 text-lg">
            Trải Nghiệm Miễn Phí <ArrowRight size={20} />
          </Link>
          <Link to="/login" className="h-14 px-8 bg-white/60 hover:bg-white border border-gray-200 text-gray-900 font-bold rounded-2xl flex items-center justify-center gap-2 backdrop-blur-md shadow-sm hover:shadow hover:-translate-y-1 transition-all duration-300 text-lg">
            Đăng Nhập <Lock size={20} className="text-gray-400" />
          </Link>
        </div>

        {/* Floating Social UI Preview Element */}
        <div className="mt-24 w-full max-w-5xl relative animate-fade-in-up" style={{ animationDelay: '500ms' }}>
          <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 rounded-3xl blur opacity-30"></div>
          <div className="relative bg-white/80 backdrop-blur-xl border border-white/40 rounded-3xl p-4 md:p-8 shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-100 pb-4 mb-6">
              <div className="flex items-center gap-2">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400"></div>
                  <div className="w-3 h-3 rounded-full bg-amber-400"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                </div>
                <div className="ml-4 h-6 w-48 bg-gray-100 rounded-md"></div>
              </div>
            </div>
            {/* Dashboard Mockup */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="col-span-2 space-y-4">
                {/* Main Stat Card */}
                <div className="bg-gradient-to-br from-[#1877F2]/10 to-[#166FE5]/5 rounded-2xl border border-[#1877F2]/20 p-6 overflow-hidden relative group/card hover:border-[#1877F2]/40 transition-colors">
                  <div className="absolute right-0 top-0 w-32 h-full bg-gradient-to-l from-white/40 to-transparent skew-x-12 translate-x-16 group-hover/card:translate-x-32 transition-transform duration-1000"></div>
                  <div className="flex items-center justify-between relative z-10">
                    <div>
                      <h4 className="text-sm font-semibold text-[#1877F2] mb-1">TỔNG LƯỢNG QUÉT</h4>
                      <p className="text-4xl font-black text-[#1C1E21] tracking-tight">2,459,102</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="flex items-center text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-md">
                          +15.4% <ArrowRight size={12} className="-rotate-45 ml-1" />
                        </span>
                        <span className="text-xs text-[#606770]">so với tuần trước</span>
                      </div>
                    </div>
                    <div className="h-16 w-16 bg-white rounded-2xl shadow-sm border border-[#E4E6EB] flex items-center justify-center shrink-0">
                      <BarChart3 className="text-[#1877F2]" size={32} />
                    </div>
                  </div>
                </div>
                
                {/* Secondary Cards */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-4">
                      <div className="h-10 w-10 bg-rose-50 rounded-xl flex items-center justify-center text-rose-500">
                        <Flame size={20} />
                      </div>
                      <span className="text-xs font-bold text-rose-500 bg-rose-50 px-2 py-1 rounded-full">Top 1 Trend</span>
                    </div>
                    <p className="text-sm text-gray-500 font-medium tracking-tight mb-1">Market Vibe Index</p>
                    <p className="text-2xl font-bold text-gray-900">89.4 / 100</p>
                    <div className="w-full bg-gray-100 rounded-full h-1.5 mt-3 overflow-hidden">
                      <div className="bg-gradient-to-r from-rose-400 to-rose-600 h-full rounded-full w-[89%]"></div>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-4">
                      <div className="h-10 w-10 bg-indigo-50 rounded-xl flex items-center justify-center text-indigo-500">
                        <Users size={20} />
                      </div>
                      <span className="text-xs font-bold text-indigo-500 bg-indigo-50 px-2 py-1 rounded-full">+12% Hiệu suất</span>
                    </div>
                    <p className="text-sm text-gray-500 font-medium tracking-tight mb-1">Chân Dung Khách Hàng</p>
                    <div className="flex -space-x-2 overflow-hidden mt-1 pt-1">
                      <div className="inline-flex h-8 w-8 rounded-full bg-gradient-to-tr from-pink-500 to-amber-400 border-2 border-white"></div>
                      <div className="inline-flex h-8 w-8 rounded-full bg-gradient-to-tr from-indigo-500 to-blue-400 border-2 border-white"></div>
                      <div className="inline-flex h-8 w-8 rounded-full bg-gradient-to-tr from-emerald-500 to-teal-400 border-2 border-white"></div>
                      <div className="inline-flex h-8 w-8 rounded-full bg-gray-50 border-2 border-white items-center justify-center text-[10px] font-bold text-gray-600">+2K</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sidebar Recent Activity */}
              <div className="col-span-1 border border-gray-100 bg-white rounded-2xl p-6 hidden md:flex flex-col shadow-sm">
                 <h4 className="font-bold text-gray-900 mb-5 text-sm uppercase tracking-wider">Trạng Thái Xử Lý Live</h4>
                 <div className="flex-1 space-y-4">
                   {[
                     { name: 'Chiến dịch Livestream', time: 'Vừa xong', progress: 100, colorClass: 'bg-emerald-50 text-emerald-500', barClass: 'bg-emerald-500' },
                     { name: 'Phân Tích Tương Tác Chỉ Định', time: '2 phút trước', progress: 78, colorClass: 'bg-blue-50 text-blue-500', barClass: 'bg-blue-500' },
                     { name: 'Thu Thập Dữ Liệu Nhóm Kín', time: '15 phút trước', progress: 45, colorClass: 'bg-indigo-50 text-indigo-500', barClass: 'bg-indigo-500' },
                     { name: 'Mã Hóa Bypass Nội Dung', time: '1 giờ trước', progress: 100, colorClass: 'bg-emerald-50 text-emerald-500', barClass: 'bg-emerald-500' },
                   ].map((job, i) => (
                     <div key={i} className="flex items-center gap-3 group">
                       <div className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform ${job.colorClass}`}>
                         {job.progress === 100 ? <CheckCircle2 size={18} /> : <Zap size={18} />}
                       </div>
                       <div className="flex-1 min-w-0">
                         <div className="flex justify-between items-end mb-1">
                           <p className="text-sm font-semibold text-gray-900 truncate">{job.name}</p>
                           <span className="text-[10px] text-gray-400 font-medium shrink-0 ml-2">{job.time}</span>
                         </div>
                         <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                           <div className={`h-full rounded-full ${job.barClass}`} style={{ width: `${job.progress}%` }}></div>
                         </div>
                       </div>
                     </div>
                   ))}
                 </div>
                 <div className="pt-4 mt-2 border-t border-gray-50 text-center">
                   <p className="text-xs font-semibold text-[#1877F2] cursor-pointer hover:underline">Xem tất cả lịch sử</p>
                 </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Features Showcase */}
      <section className="relative z-10 py-32 bg-white px-6 md:px-12 xl:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-3xl md:text-5xl font-black text-gray-900 mb-6">Giải Pháp Toàn Diện Cho Doanh Nghiệp.</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Hệ sinh thái công cụ cao cấp giúp tối ưu hóa hiệu suất làm việc, mở rộng quy mô tiếp cận và quản trị rủi ro trên mọi nền tảng.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard 
              delay={100}
              icon={Search} 
              title="Phân Tích Thông Minh" 
              description="Thu thập và bóc tách dữ liệu tương tác từ bất kỳ cá nhân, bài viết hay Group nào một cách tự động và trực quan."
            />
            <FeatureCard 
              delay={200}
              icon={Zap} 
              title="Truy Xuất Tốc Độ Cao" 
              description="Phát hiện nhanh chóng các xu hướng nội dung và truy xuất hàng loạt kết quả theo bộ lọc chuyên sâu."
            />
            <FeatureCard 
              delay={300}
              icon={Shield} 
              title="Tối Ưu Hóa Nội Dung" 
              description="Hệ thống thay thế ngôn từ thông minh giúp nội dung duy trì được sự tự nhiên, đáp ứng tiêu chuẩn khắt khe của các nền tảng."
            />
            <FeatureCard 
              delay={100}
              icon={Flame} 
              title="Phân Tích Xu Hướng" 
              description="Tự động đánh giá và chấm điểm mức độ tương tác của thị trường thông qua thuật toán máy học chuyên sâu."
            />
            <FeatureCard 
              delay={200}
              icon={Database} 
              title="Quản Trị Hệ Thống An Toàn" 
              description="Môi trường giả lập bảo mật cao đan xen vòng lặp tự động, giảm thiểu tối đa rủi ro cho hạ tầng dữ liệu và danh tính."
            />
            <FeatureCard 
              delay={300}
              icon={CheckCircle2} 
              title="Trích Xuất Dữ Liệu Đồng Bộ" 
              description="Hỗ trợ kết xuất các tập dữ liệu người dùng phức tạp về máy trạm nội bộ với định dạng chuẩn mực, thao tác tinh gọn."
            />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 bg-gray-900 border-t border-white/10 pt-20 pb-10 px-6 md:px-12 xl:px-24 text-gray-400">
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          <div>
            <div className="flex items-center gap-2 mb-6">
              <div className="h-8 w-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <Flame className="text-white" size={18} />
              </div>
              <span className="text-xl font-bold text-white">{siteSettings.site_name?.split(' ')[0] || 'TriTechz'}</span>
            </div>
            <p className="text-sm leading-relaxed mb-6">
              Cung cấp nền tảng quản trị thông tin và tối ưu vị thế truyền thông chuyên nghiệp trên không gian mạng xã hội.
            </p>
          </div>
          <div>
             <h4 className="text-white font-semibold mb-4">Sản Phẩm</h4>
             <ul className="space-y-3 text-sm">
                <li><Link to="#" className="hover:text-white transition-colors">Phân tích Facebook</Link></li>
                <li><Link to="#" className="hover:text-white transition-colors">Bypass Content AI</Link></li>
                <li><Link to="#" className="hover:text-white transition-colors">Market Trend Search</Link></li>
             </ul>
          </div>
          <div>
             <h4 className="text-white font-semibold mb-4">Công Ty</h4>
             <ul className="space-y-3 text-sm">
                <li><Link to="#" className="hover:text-white transition-colors">Giới Thiệu</Link></li>
                <li><Link to="#" className="hover:text-white transition-colors">Bảng Giá</Link></li>
                <li><Link to="#" className="hover:text-white transition-colors">Đội Ngũ</Link></li>
             </ul>
          </div>
          <div>
             <h4 className="text-white font-semibold mb-4">Hỗ Trợ</h4>
             <ul className="space-y-3 text-sm">
                <li><Link to="#" className="hover:text-white transition-colors">Help Center</Link></li>
                <li><Link to="#" className="hover:text-white transition-colors">Tài Liệu Hướng Dẫn</Link></li>
                <li><Link to="#" className="hover:text-white transition-colors">Contact Admin</Link></li>
             </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto pt-8 border-t border-gray-800 text-sm flex flex-col md:flex-row items-center justify-between">
          <p>{siteSettings.footer_copyright || '\u00A9 2026 TriTechz Analytics. All rights reserved.'}</p>
          <div className="flex gap-4 mt-4 md:mt-0">
             <Link to="#" className="hover:text-white transition-colors">Chính sách bảo mật</Link>
             <Link to="#" className="hover:text-white transition-colors">Điều khoản dịch vụ</Link>
          </div>
        </div>
      </footer>

      {/* Global CSS required for keyframes */}
      <style dangerouslySetInnerHTML={{__html: `
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob {
          animation: blob 7s infinite alternate ease-in-out;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-up {
          animation: fadeInUp 0.8s forwards cubic-bezier(0.16, 1, 0.3, 1);
          opacity: 0;
        }
      `}} />
    </div>
  );
}
